import { Banknote, Download, Eye, HandCoins, Search, WalletCards, X } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from '../app/AppShell';
import {
  FeedbackMessage,
  FilterField,
  FilterPanel,
  ModalBackdrop,
  PaginationFooter,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import { fetchLoans } from '../services/loanService';
import {
  createSupplierSavingsRelease,
  fetchSupplierBalancings,
  fetchSupplierSavingsReleases,
  reverseSupplierSavingsRelease,
} from '../services/purchaseOrderService';
import { subscribeToPurchaseOrderChanges } from '../services/purchaseOrderRealtimeService';
import { formatPeso, formatShortDate } from '../utils/formatters';

const initialFilterValues = {
  search: '',
  status: 'All',
};
const supplierSummaryPageSize = 5;
const detailsPageSize = 5;

function SupplierSavingsPage({ navigationItems, activePath, onNavigate }) {
  const [records, setRecords] = useState([]);
  const [releases, setReleases] = useState([]);
  const [cashAdvanceRecords, setCashAdvanceRecords] = useState([]);
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSupplierSummary, setSelectedSupplierSummary] = useState(null);
  const [releaseSupplierSummary, setReleaseSupplierSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReleasing, setIsReleasing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [realtimeMessage, setRealtimeMessage] = useState('');
  const [pendingRelease, setPendingRelease] = useState(null);
  const [pendingReceiptDownload, setPendingReceiptDownload] = useState(null);
  const [pendingReversal, setPendingReversal] = useState(null);

  const loadSavingsRecords = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setLoadError('');

    try {
      const [balancingResponse, releaseResponse, loanResponse] = await Promise.all([
        fetchSupplierBalancings(),
        fetchSupplierSavingsReleases(),
        fetchLoans(),
      ]);

      setRecords(Array.isArray(balancingResponse) ? balancingResponse : []);
      setReleases(Array.isArray(releaseResponse) ? releaseResponse : []);
      setCashAdvanceRecords(Array.isArray(loanResponse) ? loanResponse : []);
    } catch (error) {
      setLoadError(error.message);
      setRecords([]);
      setReleases([]);
      setCashAdvanceRecords([]);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadSavingsRecords();
  }, [loadSavingsRecords]);

  useEffect(() => {
    return subscribeToPurchaseOrderChanges((payload) => {
      if (shouldRefreshSupplierSavingsForRealtimePayload(payload)) {
        loadSavingsRecords(false).then(() => {
          setRealtimeMessage('Supplier Savings refreshed with the latest data.');
        });
      }
    });
  }, [loadSavingsRecords]);

  const releaseDetailsByBalancingId = useMemo(
    () => buildReleaseDetailsByBalancingId(releases, records),
    [records, releases]
  );
  const savingsRecords = useMemo(
    () =>
      records
        .filter((record) => isSavingsEligible(record))
        .map((record) => {
          const releaseDetails = releaseDetailsByBalancingId.get(Number(record.id));
          const savingsTotal = getRecordSavingsTotal(record);
          const savingsReleasedAmount = Math.min(
            savingsTotal,
            toNumber(releaseDetails?.releasedAmount)
          );
          const savingsRemainingAmount = Math.max(
            savingsTotal - savingsReleasedAmount,
            0
          );

          return {
            ...record,
            savingsReleaseStatus:
              savingsRemainingAmount <= 0 && releaseDetails ? 'Released' : 'Available',
            savingsReleaseType: releaseDetails?.releaseType || '',
            savingsReleasedAmount,
            savingsRemainingAmount,
          };
        })
        .sort((firstRecord, secondRecord) =>
          String(firstRecord.supplierName || '').localeCompare(
            String(secondRecord.supplierName || ''),
            undefined,
            { sensitivity: 'base' }
          )
        ),
    [records, releaseDetailsByBalancingId]
  );
  const supplierSummaries = useMemo(
    () => buildSupplierSavingsSummaries(savingsRecords),
    [savingsRecords]
  );
  const supplierSearchSuggestions = useMemo(
    () => buildSupplierSavingsSearchSuggestions(supplierSummaries),
    [supplierSummaries]
  );
  const filteredRecords = useMemo(
    () => filterSavingsRecords(savingsRecords, filterValues),
    [filterValues, savingsRecords]
  );
  const filteredSupplierSummaries = useMemo(
    () => buildSupplierSavingsSummaries(filteredRecords),
    [filteredRecords]
  );
  const totalPages = Math.max(
    1,
    Math.ceil(filteredSupplierSummaries.length / supplierSummaryPageSize)
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * supplierSummaryPageSize;
  const endIndex = Math.min(
    startIndex + supplierSummaryPageSize,
    filteredSupplierSummaries.length
  );
  const paginatedSupplierSummaries = filteredSupplierSummaries.slice(startIndex, endIndex);
  const totalSavings = filteredRecords.reduce(
    (total, record) => total + getRecordCurrentSavingsTotal(record),
    0
  );
  const currentFilteredRecordCount = filteredRecords.filter(
    (record) => getSavingsReleaseStatus(record) === 'Available'
  ).length;

  useEffect(() => {
    if (!selectedSupplierSummary) {
      return;
    }

    const nextSelectedSummary = supplierSummaries.find((summary) =>
      isSameSupplierSummary(summary, selectedSupplierSummary)
    );

    if (!nextSelectedSummary) {
      setSelectedSupplierSummary(null);
      return;
    }

    if (nextSelectedSummary !== selectedSupplierSummary) {
      setSelectedSupplierSummary(nextSelectedSummary);
    }
  }, [selectedSupplierSummary, supplierSummaries]);

  const toasts = [
    loadError ? { id: 'supplier-savings-load-error', tone: 'error', message: loadError } : null,
    actionError
      ? { id: 'supplier-savings-action-error', tone: 'error', message: actionError }
      : null,
    actionMessage
      ? { id: 'supplier-savings-action-success', tone: 'success', message: actionMessage }
      : null,
    realtimeMessage
      ? { id: 'supplier-savings-realtime-refresh', tone: 'info', message: realtimeMessage }
      : null,
  ].filter(Boolean);

  useEffect(() => {
    if (!realtimeMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setRealtimeMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [realtimeMessage]);

  useEffect(() => {
    if (!actionMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setActionMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [actionMessage]);

  useEffect(() => {
    if (!actionError) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setActionError('');
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [actionError]);

  function handleOpenReleaseConfirmation(summary, releaseType) {
    setActionError('');
    setActionMessage('');
    setPendingRelease({ summary, releaseType });
  }

  async function handleConfirmRelease() {
    if (!pendingRelease || isReleasing) {
      return;
    }

    setIsReleasing(true);
    setActionError('');
    setActionMessage('');

    try {
      const availableRecords = getAvailableSavingsRecords(pendingRelease.summary.records);
      const createdRelease = await createSupplierSavingsRelease({
        supplierId: pendingRelease.summary.supplierId,
        releaseType: pendingRelease.releaseType,
        supplierBalancingIds: availableRecords.map((record) => record.id),
      });
      const [releaseResponse, loanResponse] = await Promise.all([
        fetchSupplierSavingsReleases(),
        fetchLoans(),
      ]);

      const nextReleases = Array.isArray(releaseResponse) ? releaseResponse : [createdRelease];
      const receiptRelease =
        nextReleases.find((release) => String(release.id || '') === String(createdRelease?.id || '')) ||
        createdRelease;

      setReleases(nextReleases);
      setCashAdvanceRecords(Array.isArray(loanResponse) ? loanResponse : []);
      setReleaseSupplierSummary(null);
      setPendingReceiptDownload({
        release: receiptRelease,
        summary: pendingRelease.summary,
        releases: nextReleases,
      });
      setPendingRelease(null);
      setActionMessage(
        `${pendingRelease.summary.supplierName} savings released as ${
          pendingRelease.releaseType === 'Cash' ? 'cash' : 'C.A. deduction'
        }.`
      );
    } catch (error) {
      setActionError(error.message);
    } finally {
      setIsReleasing(false);
    }
  }

  function handleOpenReversalConfirmation(record) {
    setActionError('');
    setActionMessage('');
    setPendingReversal(record);
  }

  function getPendingReleaseAmount() {
    if (!pendingRelease) {
      return 0;
    }

    if (pendingRelease.releaseType !== 'Cash Advance') {
      return pendingRelease.summary.availableSavings;
    }

    return Math.min(
      pendingRelease.summary.availableSavings,
      getSupplierOpenCashAdvanceTotal(
        cashAdvanceRecords,
        pendingRelease.summary.supplierId
      )
    );
  }

  async function handleConfirmReversal() {
    if (!pendingReversal || isReleasing) {
      return;
    }

    setIsReleasing(true);
    setActionError('');
    setActionMessage('');

    try {
      await reverseSupplierSavingsRelease({
        releaseId: pendingReversal.id,
      });
      const [releaseResponse, loanResponse] = await Promise.all([
        fetchSupplierSavingsReleases(),
        fetchLoans(),
      ]);

      setReleases(Array.isArray(releaseResponse) ? releaseResponse : []);
      setCashAdvanceRecords(Array.isArray(loanResponse) ? loanResponse : []);
      setPendingReversal(null);
      setActionMessage(
        `${formatPesoWithDecimals(pendingReversal.amount)} supplier savings release reversed.`
      );
    } catch (error) {
      setActionError(error.message);
    } finally {
      setIsReleasing(false);
    }
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setCurrentPage(1);
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
  }

  function handleFilterReset() {
    setFilterValues(initialFilterValues);
    setCurrentPage(1);
  }

  return (
    <AppShell navigationItems={navigationItems} activePath={activePath} onNavigate={onNavigate}>
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div>
            <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
              Supplier Savings
            </h2>
            <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
              Track savings created from supplier balancing records with savings enabled.
            </p>
          </div>

          <FilterPanel
            onSubmit={handleFilterSubmit}
            onReset={handleFilterReset}
            showApply={false}
            layoutClassName="grid gap-2.5 md:grid-cols-[minmax(260px,520px)_minmax(0,1fr)_190px_auto]"
          >
            <FilterField
              label="Search"
              name="search"
              type="search"
              value={filterValues.search}
              onChange={handleFilterChange}
              placeholder="Search supplier"
              suggestions={supplierSearchSuggestions}
            />
            <span className="hidden md:block" aria-hidden="true" />
            <FilterField
              label="Status"
              name="status"
              value={filterValues.status}
              onChange={handleFilterChange}
              options={[
                { value: 'All', label: 'All Statuses' },
                { value: 'Available', label: 'Unreleased' },
                { value: 'Released', label: 'Released' },
              ]}
            />
          </FilterPanel>

          {isLoading ? (
            <FeedbackMessage tone="info" message="Loading supplier savings..." />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard label="Suppliers with Savings" value={supplierSummaries.length} />
                <SummaryCard label="Filtered Current Records" value={currentFilteredRecordCount} />
                <SummaryCard label="Filtered Current Savings Total" value={formatPeso(totalSavings)} />
                <SummaryCard
                  label="All Current Savings Total"
                  value={formatPeso(
                    savingsRecords.reduce(
                      (total, record) => total + getRecordCurrentSavingsTotal(record),
                      0
                    )
                  )}
                />
              </div>

              <SupplierSummaryGrid summaries={filteredSupplierSummaries} />

              <section className="grid gap-3">
                <div>
                  <h3 className="m-0 text-[0.98rem] font-semibold text-[var(--theme-text)]">
                    Supplier Summary Savings List
                  </h3>
                  <p className="m-0 mt-1 text-[0.86rem] text-[var(--theme-text-muted)]">
                    Review every supplier with savings and open the details for each record.
                  </p>
                </div>
                <SupplierSummaryTable
                  summaries={paginatedSupplierSummaries}
                  onViewDetails={setSelectedSupplierSummary}
                  onReleaseSavings={setReleaseSupplierSummary}
                />
              </section>

              <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                <PaginationFooter
                  currentPage={safeCurrentPage}
                  endItem={filteredSupplierSummaries.length ? endIndex : 0}
                  isNextDisabled={safeCurrentPage >= totalPages}
                  isPreviousDisabled={safeCurrentPage <= 1}
                  onNext={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  onPrevious={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  startItem={filteredSupplierSummaries.length ? startIndex + 1 : 0}
                  totalItems={filteredSupplierSummaries.length}
                  totalPages={totalPages}
                />
              </div>
            </>
          )}
        </div>
      </section>
      {selectedSupplierSummary ? (
        <SupplierSavingsDetailsModal
          summary={selectedSupplierSummary}
          releases={releases}
          onRequestReverse={handleOpenReversalConfirmation}
          onClose={() => setSelectedSupplierSummary(null)}
        />
      ) : null}
      {releaseSupplierSummary ? (
        <SavingsReleaseChoiceModal
          summary={releaseSupplierSummary}
          openCashAdvanceBalance={getSupplierOpenCashAdvanceTotal(
            cashAdvanceRecords,
            releaseSupplierSummary.supplierId
          )}
          onRequestRelease={handleOpenReleaseConfirmation}
          onClose={() => setReleaseSupplierSummary(null)}
        />
      ) : null}
      <SaveConfirmationModal
        isOpen={Boolean(pendingRelease)}
        isConfirming={isReleasing}
        title="Confirm savings release"
        message={
          pendingRelease
            ? `Release ${formatPesoWithDecimals(
                getPendingReleaseAmount()
              )} for ${pendingRelease.summary.supplierName} as ${
                pendingRelease.releaseType === 'Cash' ? 'cash' : 'C.A. deduction'
              }? ${
                pendingRelease.releaseType === 'Cash Advance'
                  ? 'Any remaining savings will stay available for cash release.'
                  : 'This will mark the available savings records as released.'
              }`
            : ''
        }
        confirmLabel="Confirm Release"
        confirmingLabel="Releasing..."
        cancelLabel="Review"
        onCancel={() => {
          if (!isReleasing) {
            setPendingRelease(null);
          }
        }}
        onConfirm={handleConfirmRelease}
      />
      <SaveConfirmationModal
        isOpen={Boolean(pendingReceiptDownload)}
        title="Download receipt?"
        message={
          pendingReceiptDownload
            ? `Download the supplier savings receipt for ${pendingReceiptDownload.summary.supplierName} now?`
            : ''
        }
        confirmLabel="Yes"
        cancelLabel="No"
        onCancel={() => setPendingReceiptDownload(null)}
        onConfirm={() => {
          if (pendingReceiptDownload) {
            downloadSupplierSavingsReceiptPdf(
              pendingReceiptDownload.release,
              pendingReceiptDownload.summary,
              pendingReceiptDownload.releases
            );
            setPendingReceiptDownload(null);
          }
        }}
      />
      <SaveConfirmationModal
        isOpen={Boolean(pendingReversal)}
        isConfirming={isReleasing}
        title="Confirm savings reversal"
        message={
          pendingReversal
            ? `Reverse ${formatPesoWithDecimals(pendingReversal.amount)} from ${
                pendingReversal.releaseType === 'Cash Advance' ? 'C.A. deduction' : 'cash release'
              }? This will make this released savings amount available again.`
            : ''
        }
        confirmLabel="Confirm Reversal"
        confirmingLabel="Reversing..."
        cancelLabel="Review"
        onCancel={() => {
          if (!isReleasing) {
            setPendingReversal(null);
          }
        }}
        onConfirm={handleConfirmReversal}
      />
    </AppShell>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-[18px] border border-[var(--theme-border-soft)] bg-white px-4 py-3.5 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.06)]">
      <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-2 block text-[1.18rem] text-[var(--theme-text-strong)]">
        {value}
      </strong>
    </div>
  );
}

function SupplierSummaryGrid({ summaries }) {
  if (!summaries.length) {
    return null;
  }

  const topSummaries = summaries.slice(0, 3);

  return (
    <section className="grid gap-3">
      <div>
        <h3 className="m-0 text-[0.98rem] font-semibold text-[var(--theme-text)]">
          Top 3 Suppliers by Savings
        </h3>
        <p className="m-0 mt-1 text-[0.86rem] text-[var(--theme-text-muted)]">
          Showing only the top 3 supplier cards. Use the summary table below for all suppliers.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {topSummaries.map((summary) => (
          <article
            key={summary.supplierKey}
            className="rounded-[18px] border border-[var(--theme-border)] bg-white p-4"
          >
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
                <HandCoins className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="m-0 truncate text-[1rem] font-semibold text-[var(--theme-text)]">
                  {summary.supplierName}
                </h3>
                <p className="m-0 mt-1 text-[0.84rem] text-[var(--theme-text-muted)]">
                  {summary.recordCount} current record{summary.recordCount === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-2 text-[0.88rem]">
              <RecordMeta label="Current Total Savings" value={formatPeso(summary.totalSavings)} />
              <RecordMeta label="Current Dry Weight Basis" value={`${formatNumber(summary.totalDryKilo)} kg`} />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function SupplierSummaryTable({ summaries, onViewDetails, onReleaseSavings }) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading className="w-[22%]">Supplier</TableHeading>
              <TableHeading className="w-[14%]">Current Records</TableHeading>
              <TableHeading className="w-[17%]">Current Total Savings</TableHeading>
              <TableHeading className="w-[22%]">Current Dry Weight Basis</TableHeading>
              <TableHeading className="w-[25%]">Action</TableHeading>
            </tr>
          </thead>
          <tbody>
            {summaries.length ? (
              summaries.map((summary) => (
                <tr
                  key={summary.supplierKey}
                  className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]"
                >
                  <TableCell>{summary.supplierName}</TableCell>
                  <TableCell>{summary.recordCount}</TableCell>
                  <TableCell>{formatPeso(summary.totalSavings)}</TableCell>
                  <TableCell>{formatNumber(summary.totalDryKilo)} kg</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                        type="button"
                        onClick={() => onViewDetails(summary)}
                      >
                        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                        View Details
                      </button>
                      <button
                        className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full bg-[var(--theme-accent)] px-3 text-[0.78rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[#d8e3e1] disabled:text-[var(--theme-text-muted)]"
                        type="button"
                        onClick={() => onReleaseSavings(summary)}
                        disabled={summary.availableSavings <= 0}
                      >
                        <HandCoins className="h-3.5 w-3.5" aria-hidden="true" />
                        Release Savings
                      </button>
                    </div>
                  </TableCell>
                </tr>
              ))
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td className="px-6 py-14" colSpan={5}>
                  <EmptyState />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 p-4 md:hidden">
        {summaries.length ? (
          summaries.map((summary) => (
            <article
              key={summary.supplierKey}
              className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong className="block text-[1rem] text-[var(--theme-text)]">
                    {summary.supplierName}
                  </strong>
                  <span className="mt-1 block text-[0.86rem] text-[var(--theme-text-muted)]">
                    {summary.recordCount} current record{summary.recordCount === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    className="grid h-9 w-9 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                    type="button"
                    onClick={() => onViewDetails(summary)}
                    aria-label={`View savings details for ${summary.supplierName}`}
                  >
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    className="grid h-9 w-9 place-items-center rounded-full bg-[var(--theme-accent)] text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[#d8e3e1] disabled:text-[var(--theme-text-muted)]"
                    type="button"
                    onClick={() => onReleaseSavings(summary)}
                    disabled={summary.availableSavings <= 0}
                    aria-label={`Release savings for ${summary.supplierName}`}
                  >
                    <HandCoins className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="mt-4 grid gap-2 text-[0.9rem]">
                <RecordMeta label="Current Total Savings" value={formatPeso(summary.totalSavings)} />
                <RecordMeta label="Current Dry Weight Basis" value={`${formatNumber(summary.totalDryKilo)} kg`} />
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function SavingsReleaseChoiceModal({
  summary,
  openCashAdvanceBalance,
  onRequestRelease,
  onClose,
}) {
  const hasOpenCashAdvance = openCashAdvanceBalance > 0;
  const hasAvailableSavings = summary.availableSavings > 0;
  const cashAdvanceDeductionAmount = Math.min(
    summary.availableSavings,
    openCashAdvanceBalance
  );
  const canDeductToCashAdvance = hasAvailableSavings && hasOpenCashAdvance;

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.26)] backdrop-blur-[3px]">
      <div className="w-full max-w-2xl overflow-hidden rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_28px_80px_rgba(var(--theme-shadow-rgb),0.24)]">
        <div className="border-b border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--theme-accent)] text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.24)]">
                <HandCoins className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="m-0 text-[1.2rem] font-semibold text-[var(--theme-text)]">
                  Release Supplier Savings
                </h3>
                <p className="m-0 mt-1 text-[0.9rem] leading-5 text-[var(--theme-text-muted)]">
                  Choose how to release savings for {summary.supplierName}.
                </p>
              </div>
            </div>
            <button
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#e1d5ca] bg-white text-[var(--theme-text-subtle)] shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close savings release options"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="grid gap-4 p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <SummaryCard label="Available Savings" value={formatPesoWithDecimals(summary.availableSavings)} />
            <SummaryCard label="Open C.A. Balance" value={formatPesoWithDecimals(openCashAdvanceBalance)} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              className="flex min-h-[120px] items-start gap-3 rounded-[18px] border border-[var(--theme-border)] bg-white p-4 text-left transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:border-[#d8dfe0] disabled:bg-[#eef1f1] disabled:text-[var(--theme-text-muted)]"
              type="button"
              disabled={!hasAvailableSavings}
              onClick={() => onRequestRelease(summary, 'Cash')}
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
                <Banknote className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-[var(--theme-text)]">Release Cash</span>
                <span className="mt-1 block text-[0.86rem] leading-5 text-[var(--theme-text-muted)]">
                  Use this when the supplier savings will be paid out as cash.
                </span>
              </span>
            </button>

            <button
              className={`flex min-h-[120px] items-start gap-3 rounded-[18px] border p-4 text-left transition ${
                canDeductToCashAdvance
                  ? 'border-[var(--theme-border)] bg-white hover:bg-[var(--theme-surface-soft)]'
                  : 'cursor-not-allowed border-[#d8dfe0] bg-[#eef1f1] text-[var(--theme-text-muted)]'
              }`}
              type="button"
              disabled={!canDeductToCashAdvance}
              onClick={() => onRequestRelease(summary, 'Cash Advance')}
              title={
                canDeductToCashAdvance
                  ? `Deduct ${formatPesoWithDecimals(cashAdvanceDeductionAmount)} from the selected supplier open C.A.`
                  : hasOpenCashAdvance
                    ? 'No available supplier savings to deduct.'
                    : 'This supplier has no open C.A. balance.'
              }
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
                <WalletCards className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-[var(--theme-text)]">Deduct to C.A.</span>
                <span className="mt-1 block text-[0.86rem] leading-5 text-[var(--theme-text-muted)]">
                  {canDeductToCashAdvance
                    ? `Apply ${formatPesoWithDecimals(cashAdvanceDeductionAmount)} against the open cash advance balance.`
                    : hasOpenCashAdvance
                      ? 'No available supplier savings to deduct.'
                      : 'Unavailable because this supplier has no open C.A. balance.'}
                </span>
              </span>
            </button>
          </div>

          <div className="flex justify-end">
            <button
              className="min-h-[42px] rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function SavingsTable({ records }) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>Supplier</TableHeading>
              <TableHeading>Balancing Record Date</TableHeading>
              <TableHeading>Date Range</TableHeading>
              <TableHeading>Total P.O.</TableHeading>
              <TableHeading>Dry Weight</TableHeading>
              <TableHeading>Savings / Kilo</TableHeading>
              <TableHeading>Current Total Savings</TableHeading>
              <TableHeading>Status</TableHeading>
            </tr>
          </thead>
          <tbody>
            {records.length ? (
              records.map((record) => (
                <tr
                  key={record.id}
                  className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]"
                >
                  <TableCell>{formatSupplierName(record)}</TableCell>
                  <TableCell>{formatShortDate(record.createdAt)}</TableCell>
                  <TableCell>{formatDateRange(record)}</TableCell>
                  <TableCell>{Array.isArray(record.purchaseOrderIds) ? record.purchaseOrderIds.length : 0}</TableCell>
                  <TableCell>{formatNumber(record.totals?.dryKilo)} kg</TableCell>
                  <TableCell>{formatPesoPerKilo(record.inputs?.savingsPerKilo || 0)}</TableCell>
                  <TableCell>{formatPesoWithDecimals(getRecordCurrentSavingsTotal(record))}</TableCell>
                  <TableCell>
                    <StatusBadge status={getSavingsReleaseDisplayStatus(record)} />
                  </TableCell>
                </tr>
              ))
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td className="px-6 py-14" colSpan={8}>
                  <EmptyState />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 p-4 md:hidden">
        {records.length ? (
          records.map((record) => (
            <article
              key={record.id}
              className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong className="block text-[1rem] text-[var(--theme-text)]">
                    {formatSupplierName(record)}
                  </strong>
                  <span className="mt-1 block text-[0.86rem] text-[var(--theme-text-muted)]">
                    {formatDateRange(record)}
                  </span>
                </div>
                <StatusBadge status={getSavingsReleaseDisplayStatus(record)} />
              </div>
              <div className="mt-4 grid gap-2 text-[0.9rem]">
                <RecordMeta label="Total P.O." value={Array.isArray(record.purchaseOrderIds) ? record.purchaseOrderIds.length : 0} />
                <RecordMeta label="Balancing Record Date" value={formatShortDate(record.createdAt)} />
                <RecordMeta label="Dry Weight" value={`${formatNumber(record.totals?.dryKilo)} kg`} />
                <RecordMeta label="Savings / Kilo" value={formatPesoPerKilo(record.inputs?.savingsPerKilo || 0)} />
                <RecordMeta label="Current Total Savings" value={formatPesoWithDecimals(getRecordCurrentSavingsTotal(record))} />
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function ReleasedSavingsTable({ releases, summary, onRequestReverse }) {
  return (
    <section className="grid gap-3">
      <div>
        <h4 className="m-0 text-[0.94rem] font-semibold text-[var(--theme-text)]">
          Released Savings History
        </h4>
        <p className="m-0 mt-1 text-[0.82rem] text-[var(--theme-text-muted)]">
          Cash releases and C.A. deductions recorded for this supplier.
        </p>
      </div>
      <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white">
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--theme-accent-panel)]">
                <TableHeading>Release Date</TableHeading>
                <TableHeading>Type</TableHeading>
                <TableHeading align="right">Released Amount</TableHeading>
                <TableHeading align="right">C.A. Applied</TableHeading>
                <TableHeading>Prepared By</TableHeading>
                <TableHeading align="right">Action</TableHeading>
              </tr>
            </thead>
            <tbody>
              {releases.length ? (
                releases.map((release) => {
                  const isReversed = isSupplierSavingsReleaseReversed(release);

                  return (
                    <tr
                      key={release.id}
                      className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]"
                    >
                      <TableCell>{formatShortDate(release.releasedAt || release.createdAt)}</TableCell>
                      <TableCell>
                        <StatusBadge
                          status={
                            isReversed
                              ? 'Reversed'
                              : release.releaseType === 'Cash Advance'
                                ? 'C.A. Deducted'
                                : 'Cash Released'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{formatPesoWithDecimals(release.amount)}</TableCell>
                      <TableCell align="right">
                        {release.releaseType === 'Cash Advance'
                          ? formatPesoWithDecimals(release.cashAdvanceAppliedAmount || release.amount)
                          : '-'}
                      </TableCell>
                      <TableCell>{release.createdByName || '-'}</TableCell>
                      <TableCell align="right">
                        <div className="flex justify-end gap-2">
                          {!isReversed ? (
                            <button
                              className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                              type="button"
                              onClick={() => onRequestReverse(release)}
                            >
                              Reverse
                            </button>
                          ) : null}
                          <button
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
                            type="button"
                            onClick={() => downloadSupplierSavingsReceiptPdf(release, summary, releases)}
                            title="Download receipt PDF"
                            aria-label="Download receipt PDF"
                          >
                            <Download className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      </TableCell>
                    </tr>
                  );
                })
              ) : (
                <tr className="border-t border-[var(--theme-border-soft)]">
                  <td className="px-6 py-8 text-center text-[0.86rem] text-[var(--theme-text-muted)]" colSpan={6}>
                    No released savings records yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 p-4 md:hidden">
          {releases.length ? (
            releases.map((release) => {
              const isReversed = isSupplierSavingsReleaseReversed(release);

              return (
                <article
                  key={release.id}
                  className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <strong className="block text-[0.95rem] text-[var(--theme-text)]">
                        {formatPesoWithDecimals(release.amount)}
                      </strong>
                      <span className="mt-1 block text-[0.8rem] text-[var(--theme-text-muted)]">
                        {formatShortDate(release.releasedAt || release.createdAt)}
                      </span>
                    </div>
                    <StatusBadge
                      status={
                        isReversed
                          ? 'Reversed'
                          : release.releaseType === 'Cash Advance'
                            ? 'C.A. Deducted'
                            : 'Cash Released'
                      }
                    />
                  </div>
                  <div className="mt-3 grid gap-1.5 text-[0.86rem]">
                    <RecordMeta
                      label="C.A. Applied"
                      value={
                        release.releaseType === 'Cash Advance'
                          ? formatPesoWithDecimals(release.cashAdvanceAppliedAmount || release.amount)
                          : '-'
                      }
                    />
                    <RecordMeta label="Prepared By" value={release.createdByName || '-'} />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {!isReversed ? (
                      <button
                        className="inline-flex min-h-[38px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                        type="button"
                        onClick={() => onRequestReverse(release)}
                      >
                        Reverse
                      </button>
                    ) : null}
                    <button
                      className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.82rem] font-semibold text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
                      type="button"
                      onClick={() => downloadSupplierSavingsReceiptPdf(release, summary, releases)}
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Download Receipt
                    </button>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="rounded-[16px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-4 py-8 text-center text-[0.86rem] text-[var(--theme-text-muted)]">
              No released savings records yet.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function SupplierSavingsDetailsModal({ summary, releases, onRequestReverse, onClose }) {
  const [detailsPage, setDetailsPage] = useState(1);
  const records = Array.isArray(summary.records) ? summary.records : [];
  const releaseRecords = getSupplierReleaseRecords(releases, summary.supplierId);
  const totalDetailsPages = Math.max(1, Math.ceil(records.length / detailsPageSize));
  const safeDetailsPage = Math.min(detailsPage, totalDetailsPages);
  const detailsStartIndex = (safeDetailsPage - 1) * detailsPageSize;
  const detailsEndIndex = Math.min(detailsStartIndex + detailsPageSize, records.length);
  const paginatedRecords = records.slice(detailsStartIndex, detailsEndIndex);

  useEffect(() => {
    setDetailsPage(1);
  }, [summary.supplierKey]);

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.26)] backdrop-blur-[3px]">
      <div className="w-full max-w-6xl overflow-hidden rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_28px_80px_rgba(var(--theme-shadow-rgb),0.24)]">
        <div className="border-b border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--theme-accent)] text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.24)]">
                <HandCoins className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="m-0 text-[1.2rem] font-semibold text-[var(--theme-text)]">
                  Supplier Savings Details
                </h3>
                <p className="m-0 mt-1 text-[0.9rem] leading-5 text-[var(--theme-text-muted)]">
                  {summary.supplierName} / {summary.totalRecordCount} record{summary.totalRecordCount === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <button
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#e1d5ca] bg-white text-[var(--theme-text-subtle)] shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close supplier savings details"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="grid max-h-[72vh] gap-4 overflow-y-auto p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <SummaryCard label="Current Total Savings" value={formatPeso(summary.totalSavings)} />
            <SummaryCard label="Current Dry Weight Basis" value={`${formatNumber(summary.totalDryKilo)} kg`} />
          </div>
          <SavingsTable records={paginatedRecords} />
          <ReleasedSavingsTable
            releases={releaseRecords}
            summary={summary}
            onRequestReverse={onRequestReverse}
          />
          <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
            <PaginationFooter
              currentPage={safeDetailsPage}
              endItem={records.length ? detailsEndIndex : 0}
              isNextDisabled={safeDetailsPage >= totalDetailsPages}
              isPreviousDisabled={safeDetailsPage <= 1}
              onNext={() =>
                setDetailsPage((page) => Math.min(totalDetailsPages, page + 1))
              }
              onPrevious={() => setDetailsPage((page) => Math.max(1, page - 1))}
              startItem={records.length ? detailsStartIndex + 1 : 0}
              totalItems={records.length}
              totalPages={totalDetailsPages}
            />
          </div>
          <div className="flex justify-end">
            <button
              className="min-h-[44px] rounded-full border border-[var(--theme-border)] bg-white px-6 text-[0.94rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function TableHeading({ children, align = 'left', className = '' }) {
  const alignClassName = align === 'right' ? 'text-right' : 'text-left';

  return (
    <th className={`whitespace-nowrap px-3 py-2.5 ${alignClassName} text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4 ${className}`}>
      {children}
    </th>
  );
}

function TableCell({ children, align = 'left', className = '' }) {
  const alignClassName = align === 'right' ? 'text-right' : 'text-left';

  return (
    <td className={`whitespace-nowrap px-3 py-2.5 ${alignClassName} align-middle text-[0.84rem] text-[#222222] lg:px-4 ${className}`}>
      {children}
    </td>
  );
}

function RecordMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[#2a2a2a]">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <Search className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No supplier savings found
      </strong>
    </div>
  );
}

function StatusBadge({ status }) {
  const colorClassName = {
    'Cash Released': 'bg-[#eef6ec] text-[#3f7a44]',
    'C.A. Deducted': 'bg-[#eef6ec] text-[#3f7a44]',
    Released: 'bg-[#eef6ec] text-[#3f7a44]',
    Reversed: 'bg-[#eef1f5] text-[#59677b]',
    Unreleased: 'bg-[#fff7e8] text-[#b57424]',
  }[status] || 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]';

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[0.8rem] font-semibold ${colorClassName}`}>
      {status || '-'}
    </span>
  );
}

function buildSupplierSavingsSummaries(records) {
  const summariesBySupplier = new Map();

  records.forEach((record) => {
    const supplierKey = String(record.supplierId || formatSupplierName(record));
    const currentSummary = summariesBySupplier.get(supplierKey) || {
      supplierKey,
      supplierId: record.supplierId || null,
      supplierName: formatSupplierName(record),
      recordCount: 0,
      totalRecordCount: 0,
      totalSavings: 0,
      availableSavings: 0,
      releasedSavings: 0,
      totalDryKilo: 0,
      records: [],
    };

    currentSummary.totalRecordCount += 1;
    const savingsTotal = getRecordSavingsTotal(record);
    const currentSavingsTotal = getRecordCurrentSavingsTotal(record);
    const releasedSavingsTotal = Math.max(savingsTotal - currentSavingsTotal, 0);

    if (getSavingsReleaseStatus(record) === 'Released') {
      currentSummary.releasedSavings += savingsTotal;
    } else {
      currentSummary.recordCount += 1;
      currentSummary.totalSavings += currentSavingsTotal;
      currentSummary.availableSavings += currentSavingsTotal;
      currentSummary.releasedSavings += releasedSavingsTotal;
      currentSummary.totalDryKilo += toNumber(record.totals?.dryKilo);
    }
    currentSummary.records.push(record);
    summariesBySupplier.set(supplierKey, currentSummary);
  });

  return Array.from(summariesBySupplier.values()).sort(
    (firstSummary, secondSummary) =>
      secondSummary.totalSavings - firstSummary.totalSavings ||
      firstSummary.supplierName.localeCompare(secondSummary.supplierName, undefined, {
        sensitivity: 'base',
      })
  );
}

function buildSupplierSavingsSearchSuggestions(summaries) {
  return summaries
    .map((summary) => ({
      group: 'supplier',
      value: summary.supplierName,
      label: summary.supplierName,
      detail: `${summary.recordCount} current record${summary.recordCount === 1 ? '' : 's'}`,
    }))
    .sort((firstSuggestion, secondSuggestion) =>
      firstSuggestion.label.localeCompare(secondSuggestion.label, undefined, {
        sensitivity: 'base',
      })
    );
}

function isSameSupplierSummary(firstSummary, secondSummary) {
  if (!firstSummary || !secondSummary) {
    return false;
  }

  if (firstSummary.supplierId || secondSummary.supplierId) {
    return String(firstSummary.supplierId || '') === String(secondSummary.supplierId || '');
  }

  return String(firstSummary.supplierKey || '') === String(secondSummary.supplierKey || '');
}

function getSupplierReleaseRecords(releases, supplierId) {
  return (Array.isArray(releases) ? releases : [])
    .filter((release) => String(release.supplierId || '') === String(supplierId || ''))
    .sort((firstRelease, secondRelease) => {
      const firstDate = new Date(firstRelease.releasedAt || firstRelease.createdAt || 0).getTime();
      const secondDate = new Date(secondRelease.releasedAt || secondRelease.createdAt || 0).getTime();

      return secondDate - firstDate || Number(secondRelease.id || 0) - Number(firstRelease.id || 0);
    });
}

function isSupplierSavingsReleaseReversed(release) {
  const supplierBalancingIds = (release?.supplierBalancingIds || [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);

  if (!supplierBalancingIds.length) {
    return false;
  }

  const reversedIds = new Set(
    (release?.reversedSupplierBalancingIds || []).map((id) => Number(id))
  );

  return supplierBalancingIds.every((id) => reversedIds.has(id));
}

function filterSavingsRecords(records, filters) {
  const normalizedSearch = String(filters.search || '').trim().toLowerCase();

  return records.filter((record) => {
    const matchesSearch =
      !normalizedSearch ||
      [
        formatSupplierName(record),
        record.status,
        formatDateRange(record),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    const matchesStatus =
      filters.status === 'All' ||
      String(getSavingsReleaseStatus(record) || '') === String(filters.status || '');

    return matchesSearch && matchesStatus;
  });
}

function shouldRefreshSupplierSavingsForRealtimePayload(payload = {}) {
  return [
    'purchase-order-supplier-balancing',
    'supplier-savings-release',
  ].includes(payload.changedEntity);
}

function buildReleaseDetailsByBalancingId(releases, records = []) {
  const releaseDetailsByBalancingId = new Map();
  const savingsTotalByBalancingId = new Map(
    (Array.isArray(records) ? records : []).map((record) => [
      Number(record.id),
      getRecordSavingsTotal(record),
    ])
  );

  (Array.isArray(releases) ? [...releases].reverse() : []).forEach((release) => {
    let remainingReleaseAmount = toNumber(release.amount);
    const reversedIds = new Set(
      (release.reversedSupplierBalancingIds || []).map((id) => Number(id))
    );

    (release.supplierBalancingIds || []).forEach((id) => {
      if (remainingReleaseAmount <= 0) {
        return;
      }

      const balancingId = Number(id);

      if (!Number.isInteger(balancingId) || balancingId <= 0 || reversedIds.has(balancingId)) {
        return;
      }

      const savingsTotal = savingsTotalByBalancingId.get(balancingId) || 0;
      const currentDetails = releaseDetailsByBalancingId.get(balancingId) || {
        releaseType: '',
        releasedAmount: 0,
      };
      const remainingSavings = Math.max(savingsTotal - currentDetails.releasedAmount, 0);
      const releasedAmount = Math.min(remainingSavings, remainingReleaseAmount);

      if (releasedAmount > 0) {
        releaseDetailsByBalancingId.set(balancingId, {
          releaseType: release.releaseType || '',
          releasedAmount: currentDetails.releasedAmount + releasedAmount,
        });
        remainingReleaseAmount -= releasedAmount;
      }
    });
  });

  return releaseDetailsByBalancingId;
}

function getSavingsReleaseStatus(record) {
  return record?.savingsReleaseStatus === 'Released' ? 'Released' : 'Available';
}

function getSavingsReleaseDisplayStatus(record) {
  if (getSavingsReleaseStatus(record) !== 'Released') {
    return 'Unreleased';
  }

  return record?.savingsReleaseType === 'Cash Advance' ? 'C.A. Deducted' : 'Cash Released';
}

function getAvailableSavingsRecords(records) {
  return (Array.isArray(records) ? records : []).filter(
    (record) => getSavingsReleaseStatus(record) === 'Available'
  );
}

function isSavingsEligible(record) {
  return String(record?.status || '') === 'Paid' && Boolean(toNumber(record?.inputs?.savingsEnabled));
}

function getRecordSavingsTotal(record) {
  return toNumber(record?.totals?.savingsTotal);
}

function getRecordCurrentSavingsTotal(record) {
  if (typeof record?.savingsRemainingAmount !== 'undefined') {
    return Math.max(toNumber(record.savingsRemainingAmount), 0);
  }

  return getSavingsReleaseStatus(record) === 'Released' ? 0 : getRecordSavingsTotal(record);
}

function getSupplierOpenCashAdvanceTotal(records, supplierId) {
  if (!supplierId) {
    return 0;
  }

  return records
    .filter(
      (record) =>
        String(record.supplierId || '') === String(supplierId) &&
        String(record.status || '') === 'Open'
    )
    .reduce(
      (total, record) =>
        total + toNumber(record.remainingAmount ?? record.amount ?? 0),
      0
    );
}

function formatSupplierName(record) {
  return [record?.sacksMark, record?.supplierName].filter(Boolean).join(' - ') || '-';
}

function formatDateRange(record) {
  if (record?.dateFrom && record?.dateTo) {
    return `${formatShortDate(record.dateFrom)} - ${formatShortDate(record.dateTo)}`;
  }

  return formatShortDate(record?.dateFrom || record?.dateTo);
}

function formatNumber(value) {
  return toNumber(value).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  });
}

function formatPesoWithDecimals(value) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatPesoPerKilo(value) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function downloadSupplierSavingsReceiptPdf(release, summary, releases = []) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pdfTheme = getPdfThemeColors();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 42;
  const contentWidth = pageWidth - margin * 2;
  const releaseTypeLabel = release.releaseType === 'Cash Advance' ? 'C.A. Deduction' : 'Cash Release';
  const releaseDate = formatShortDate(release.releasedAt || release.createdAt);
  const receiptNumber = buildSupplierSavingsReceiptNumber(release);
  const supplierName = summary?.supplierName || release.supplierName || '-';
  const preparedBy = release.createdByName || '-';
  const releaseAmount = toNumber(release.amount);
  const cashAdvanceAmount =
    release.releaseType === 'Cash Advance'
      ? toNumber(release.cashAdvanceAppliedAmount || release.amount)
      : 0;
  const savingsLedger = buildSupplierSavingsReceiptLedger(release, summary, releases);
  let y = 44;

  setPdfFillColor(doc, pdfTheme.accentStrong);
  doc.roundedRect(margin, y, contentWidth, 88, 16, 16, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('SUPPLIER SAVINGS RELEASE RECEIPT', margin + 22, y + 28);
  doc.setFontSize(21);
  doc.text(receiptNumber, margin + 22, y + 58);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(releaseTypeLabel, pageWidth - margin - 22, y + 30, { align: 'right' });
  doc.text(releaseDate, pageWidth - margin - 22, y + 50, { align: 'right' });

  y += 112;
  drawSupplierSavingsPdfSectionTitle(doc, 'Release Details', margin, y, pdfTheme);
  y += 24;
  drawSupplierSavingsPdfInfoBox(
    doc,
    [
      ['Supplier', supplierName],
      ['Prepared By', preparedBy],
      ['Transaction Date', releaseDate],
      ['Transaction Type', releaseTypeLabel],
    ],
    margin,
    y,
    contentWidth,
    pdfTheme
  );

  y += 126;
  drawSupplierSavingsPdfSectionTitle(doc, 'Release Summary', margin, y, pdfTheme);
  y += 24;
  drawSupplierSavingsPdfAmountRow(doc, 'Savings Released', releaseAmount, margin, y, contentWidth, pdfTheme, true);
  y += 38;
  drawSupplierSavingsPdfAmountRow(
    doc,
    'Savings Balance Before Release',
    savingsLedger.savingsBeforeRelease,
    margin,
    y,
    contentWidth,
    pdfTheme
  );
  y += 38;
  drawSupplierSavingsPdfAmountRow(
    doc,
    'Savings Balance After Release',
    savingsLedger.runningSavingsAfterRelease,
    margin,
    y,
    contentWidth,
    pdfTheme,
    true
  );
  y += 38;
  drawSupplierSavingsPdfAmountRow(
    doc,
    'Current Savings Balance',
    savingsLedger.currentSavings,
    margin,
    y,
    contentWidth,
    pdfTheme
  );
  y += 38;
  drawSupplierSavingsPdfAmountRow(
    doc,
    'Applied to C.A.',
    cashAdvanceAmount,
    margin,
    y,
    contentWidth,
    pdfTheme,
    release.releaseType === 'Cash Advance'
  );
  y += 38;
  drawSupplierSavingsPdfAmountRow(
    doc,
    'Cash Payout',
    release.releaseType === 'Cash Advance' ? 0 : releaseAmount,
    margin,
    y,
    contentWidth,
    pdfTheme,
    release.releaseType !== 'Cash Advance'
  );

  y += 44;
  setPdfDrawColor(doc, pdfTheme.borderSoft);
  doc.line(margin, y, pageWidth - margin, y);
  y += 24;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setPdfTextColor(doc, pdfTheme.textMuted);
  doc.text(
    'This receipt confirms the supplier savings release recorded in Seaweeds V3 Operations.',
    margin,
    y
  );

  y += 70;
  setPdfDrawColor(doc, pdfTheme.borderSoft);
  doc.line(pageWidth - margin - 180, y, pageWidth - margin, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setPdfTextColor(doc, pdfTheme.text);
  doc.text('Authorized Signature', pageWidth - margin, y + 16, { align: 'right' });

  doc.save(`${slugify(`supplier-savings-receipt-${supplierName}-${receiptNumber}`)}.pdf`);
}

function drawSupplierSavingsPdfSectionTitle(doc, label, x, y, pdfTheme) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setPdfTextColor(doc, pdfTheme.accentStrong);
  doc.text(String(label || '').toUpperCase(), x, y);
}

function drawSupplierSavingsPdfInfoBox(doc, rows, x, y, width, pdfTheme) {
  setPdfDrawColor(doc, pdfTheme.borderSoft);
  doc.roundedRect(x, y, width, 96, 14, 14, 'S');

  rows.forEach(([label, value], index) => {
    const rowY = y + 24 + index * 18;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setPdfTextColor(doc, pdfTheme.textMuted);
    doc.text(label, x + 18, rowY);

    doc.setFont('helvetica', 'normal');
    setPdfTextColor(doc, pdfTheme.text);
    doc.text(String(value || '-'), x + 132, rowY);
  });
}

function drawSupplierSavingsPdfAmountRow(doc, label, amount, x, y, width, pdfTheme, isEmphasized = false) {
  if (isEmphasized) {
    setPdfFillColor(doc, pdfTheme.surfaceSoft);
    doc.roundedRect(x, y - 18, width, 30, 10, 10, 'F');
  }

  doc.setFont('helvetica', isEmphasized ? 'bold' : 'normal');
  doc.setFontSize(isEmphasized ? 11 : 10);
  setPdfTextColor(doc, pdfTheme.text);
  doc.text(label, x + 18, y);
  doc.text(formatPdfPeso(amount), x + width - 18, y, { align: 'right' });
}

function buildSupplierSavingsReceiptLedger(targetRelease, summary, releases = []) {
  const records = Array.isArray(summary?.records) ? summary.records : [];
  const originalSavingsByBalancingId = new Map(
    records.map((record) => [Number(record.id), getRecordSavingsTotal(record)])
  );
  const originalSavingsTotal = records.reduce(
    (total, record) => total + getRecordSavingsTotal(record),
    0
  );
  const releaseRecords = (Array.isArray(releases) ? releases : [])
    .filter((release) => String(release.supplierId || '') === String(summary?.supplierId || ''))
    .sort((firstRelease, secondRelease) => {
      const firstDate = new Date(firstRelease.releasedAt || firstRelease.createdAt || 0).getTime();
      const secondDate = new Date(secondRelease.releasedAt || secondRelease.createdAt || 0).getTime();

      return firstDate - secondDate || Number(firstRelease.id || 0) - Number(secondRelease.id || 0);
    });
  const releasedByBalancingId = new Map();
  let cumulativeReleasedAmount = 0;
  let targetSavingsBeforeRelease = originalSavingsTotal;
  let targetRunningSavingsAfterRelease = originalSavingsTotal;
  let hasFoundTargetRelease = false;

  releaseRecords.forEach((release) => {
    const savingsBeforeRelease = Math.max(originalSavingsTotal - cumulativeReleasedAmount, 0);
    const activeReleaseAmount = getActiveSupplierSavingsReleaseAmount(
      release,
      originalSavingsByBalancingId,
      releasedByBalancingId
    );
    const runningSavingsAfterRelease = Math.max(savingsBeforeRelease - activeReleaseAmount, 0);

    if (String(release.id || '') === String(targetRelease?.id || '')) {
      targetSavingsBeforeRelease = savingsBeforeRelease;
      targetRunningSavingsAfterRelease = runningSavingsAfterRelease;
      hasFoundTargetRelease = true;
    }

    cumulativeReleasedAmount += activeReleaseAmount;
  });

  const fallbackCurrentSavings = Math.max(originalSavingsTotal - cumulativeReleasedAmount, 0);

  return {
    currentSavings: fallbackCurrentSavings,
    savingsBeforeRelease: hasFoundTargetRelease
      ? targetSavingsBeforeRelease
      : fallbackCurrentSavings + toNumber(targetRelease?.amount),
    runningSavingsAfterRelease: hasFoundTargetRelease
      ? targetRunningSavingsAfterRelease
      : fallbackCurrentSavings,
  };
}

function getActiveSupplierSavingsReleaseAmount(release, originalSavingsByBalancingId, releasedByBalancingId) {
  let remainingReleaseAmount = toNumber(release?.amount);
  let activeReleaseAmount = 0;
  const reversedIds = new Set(
    (release?.reversedSupplierBalancingIds || []).map((id) => Number(id))
  );

  (release?.supplierBalancingIds || []).forEach((id) => {
    if (remainingReleaseAmount <= 0) {
      return;
    }

    const balancingId = Number(id);

    if (!Number.isInteger(balancingId) || balancingId <= 0 || reversedIds.has(balancingId)) {
      return;
    }

    const originalSavings = originalSavingsByBalancingId.get(balancingId) || 0;
    const alreadyReleased = releasedByBalancingId.get(balancingId) || 0;
    const remainingSavings = Math.max(originalSavings - alreadyReleased, 0);
    const appliedAmount = Math.min(remainingSavings, remainingReleaseAmount);

    if (appliedAmount > 0) {
      releasedByBalancingId.set(balancingId, alreadyReleased + appliedAmount);
      activeReleaseAmount += appliedAmount;
      remainingReleaseAmount -= appliedAmount;
    }
  });

  return activeReleaseAmount;
}

function buildSupplierSavingsReceiptNumber(release) {
  return `SSR-${String(release.id || '0').padStart(5, '0')}`;
}

function formatPdfPeso(value) {
  return `PHP ${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getPdfThemeColors() {
  const readThemeColor = (propertyName, fallback) => {
    if (typeof window === 'undefined' || !window.getComputedStyle || !document?.documentElement) {
      return fallback;
    }

    return window.getComputedStyle(document.documentElement).getPropertyValue(propertyName).trim() || fallback;
  };

  return {
    accentStrong: parseCssColor(readThemeColor('--theme-accent-strong', '#467067')),
    borderSoft: parseCssColor(readThemeColor('--theme-border-soft', '#cddbd7')),
    surfaceSoft: parseCssColor(readThemeColor('--theme-surface-soft', '#f0f8f1')),
    text: parseCssColor(readThemeColor('--theme-text', '#364745')),
    textMuted: parseCssColor(readThemeColor('--theme-text-muted', '#587579')),
  };
}

function setPdfFillColor(doc, color) {
  doc.setFillColor(color.red, color.green, color.blue);
}

function setPdfTextColor(doc, color) {
  doc.setTextColor(color.red, color.green, color.blue);
}

function setPdfDrawColor(doc, color) {
  doc.setDrawColor(color.red, color.green, color.blue);
}

function parseCssColor(value) {
  const normalizedValue = String(value || '').trim();
  const hexMatch = normalizedValue.match(/^#([a-f\d]{3}|[a-f\d]{6})$/i);

  if (hexMatch) {
    const hexValue =
      hexMatch[1].length === 3
        ? hexMatch[1].split('').map((character) => `${character}${character}`).join('')
        : hexMatch[1];

    return {
      red: parseInt(hexValue.slice(0, 2), 16),
      green: parseInt(hexValue.slice(2, 4), 16),
      blue: parseInt(hexValue.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalizedValue.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);

  if (rgbMatch) {
    return {
      red: Number(rgbMatch[1]),
      green: Number(rgbMatch[2]),
      blue: Number(rgbMatch[3]),
    };
  }

  return { red: 54, green: 71, blue: 69 };
}

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toNumber(value) {
  const numericValue = Number(value || 0);

  return Number.isFinite(numericValue) ? numericValue : 0;
}

export default SupplierSavingsPage;
